<template>
  <ul v-if="objectives.length > 0" class="mx-auto flex w-full max-w-3xl flex-col gap-2">
    <li v-for="objective in objectives" :key="objective.id">
      <ObjectiveItem
        :objective="objective"
        :feedback-topics="feedbackTopics"
        :feedback-topic="
          feedbackTopics.find((feedbackTopic) => feedbackTopic.id === objective.feedbackTopicId)
        "
      />
    </li>
  </ul>
  <EmptyPage
    v-else
    :title="$t('coaching.objectives.empty.title')"
    :description="$t('coaching.objectives.empty.description')"
    icon="target"
  >
    <div class="mb-10">
      <BaseTextButton color="tonal" icon="add" size="M" @click="emit('create')">{{
        $t('objective.create.action')
      }}</BaseTextButton>
    </div>
  </EmptyPage>

  <BaseDialog
    type="basic"
    :title="$t('objective.create.title')"
    :description="$t('objective.create.description')"
    :is-open="showForm"
    class="md:min-w-[500px]"
    @close="emit('cancel-create')"
  >
    <div class="flex flex-col gap-1">
      <BaseInputField
        type="text"
        required
        v-model="creatableObjective.title"
        :label="$t('objective.create.name')"
      />
      <BaseInputField
        type="multiText"
        v-model="creatableObjective.description"
        :label="$t('objective.create.descriptionLabel')"
      />
      <BaseInputSelect
        v-model="creatableObjective.feedbackTopicId"
        :label="$t('objective.create.feedbackTopic')"
        :items="[
          {
            id: undefined,
            title: t('objective.create.noFeedbackTopic'),
            selected: !creatableObjective.feedbackTopicId,
            leadingIcon: 'remove',
          },
          ...selectableFeedbackTopics.map((feedbackTopic) => ({
            id: feedbackTopic.id,
            title: feedbackTopic.title,
            leadingIcon: feedbackTopic.icon,
            selected: feedbackTopic.id === creatableObjective.feedbackTopicId,
          })),
        ]"
      />
    </div>
    <template #dismiss>
      <BaseTextButton color="text" size="M" @click="emit('cancel-create')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton
        color="filled"
        size="M"
        :disabled="!creatableObjective.title"
        @click="emit('save')"
      >
        {{ $t('objective.create.action') }}</BaseTextButton
      >
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ObjectiveItem from '@/core/components/objective/ObjectiveItem.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseInputSelect from '@/core/components/base/form-fields/BaseInputSelect.vue'
  import EmptyPage from '@/core/components/empty-states/EmptyPage.vue'
  import type { CreateableObjective, Objective } from '@/core/composables/use-objectives'
  import type { CompleteFeedbackTopic } from '@/core/composables/use-complete-feedback-topics'
  import { withoutCoachOnlyTopics } from '@/core/models/feedback-topic'
  import type { ApiRequestStatus } from '@/core/models/api/api-request-status'

  const props = defineProps<{
    status: ApiRequestStatus
    objectives: ReadonlyArray<Objective>
    feedbackTopics: ReadonlyArray<CompleteFeedbackTopic>
    showForm: boolean
    creatableObjective: Partial<CreateableObjective>
  }>()

  const selectableFeedbackTopics = computed(() => withoutCoachOnlyTopics(props.feedbackTopics))

  const emit = defineEmits<{
    create: []
    'cancel-create': []
    save: []
  }>()

  const { t } = useI18n()
</script>
